import { useApiRequest } from '@/shared/api/useApiRequest'
import { getApiBaseUrl } from '@/api/base-url'

export interface SearchFilters {
  category?: number | string
  manufacturer?: number | string
  search?: string
  min_price?: number
  max_price?: number
  in_stock?: boolean
}

export interface SortOptions {
  by: 'name' | 'price' | 'created_at' | 'view_count'
  order: 'asc' | 'desc'
}

export interface PaginationParams {
  page?: number
  limit?: number
}

export interface Product {
  id: string | number
  name: string
  sku: string
  price: number
  stock: number
  is_available: boolean
  view_count: number
  category: {
    id: number
    name: string
    slug: string
  }
  manufacturer: {
    id: number
    name: string
    slug: string
  }
  description?: string
  images?: string[]
  primary_image_url?: string
  specifications?: Record<string, string | number | boolean>
  created_at?: string
  updated_at?: string
  documents?: Array<{
    id: string
    name: string
    url: string
    type: string
    size: number
  }>
}

export interface ProductReview {
  id: number
  author_name: string
  title: string | null
  comment: string
  rating: number
  pros: string[]
  cons: string[]
  is_verified: boolean
  created_at: string | null
}

export interface SearchResponse {
  products: Product[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  filters: SearchFilters
  sort: SortOptions
}

export interface QuickSearchResponse {
  query: string
  products: Product[]
  count: number
}

export interface AdminProductPayload {
  name: string
  sku?: string
  description?: string
  price: number
  tax_rate: number
  stock: number
  specifications?: Record<string, string | number | boolean | null>
  category_id?: number
  manufacturer_id?: number
}

interface ApiEnvelope<T> {
  success?: boolean
  data?: T
  message?: string
}

function throwIfApiError(response: unknown): void {
  if (
    response &&
    typeof response === 'object' &&
    'success' in response &&
    response.success === false
  ) {
    const message =
      'message' in response && typeof response.message === 'string'
        ? response.message
        : 'error' in response && typeof response.error === 'string'
          ? response.error
          : 'API request failed'

    throw new Error(message)
  }
}

/**
 * API client для ProductSearchService
 * Интегрируется с backend/src/ProductBundle/Service/ProductSearchService.php
 */
export function useProductAPI() {
  const { makeRequest } = useApiRequest()

  /**
   * Поиск товаров с фильтрами, сортировкой и пагинацией
   * Backend: ProductSearchService::searchProducts()
   */
  const searchProducts = async (
    filters: SearchFilters = {},
    sort: SortOptions = { by: 'name', order: 'asc' },
    pagination: PaginationParams = { page: 1, limit: 20 },
  ): Promise<SearchResponse> => {
    const params: Record<string, string | number | boolean> = {
      ...filters,
      sort_by: sort.by,
      sort_order: sort.order,
    }

    if (pagination.page) params['page'] = pagination.page
    if (pagination.limit) params['limit'] = pagination.limit

    return await makeRequest<SearchResponse>(
      '/api/products',
      {
        method: 'GET',
      },
      params,
    )
  }

  /**
   * Быстрый поиск (для автодополнения)
   * Backend: ProductSearchService::quickSearch()
   */
  const quickSearch = async (query: string, limit: number = 10): Promise<QuickSearchResponse> => {
    return await makeRequest<QuickSearchResponse>(
      '/api/products/search',
      {
        method: 'GET',
      },
      { q: query, limit },
    )
  }

  /**
   * Получение популярных товаров
   * Backend: ProductSearchService::getPopularProducts()
   */
  const getPopularProducts = async (limit: number = 10): Promise<Product[]> => {
    return await makeRequest<Product[]>(
      '/api/products/popular',
      {
        method: 'GET',
      },
      { limit },
    )
  }

  /**
   * Получение одного товара по ID
   * Backend: через ProductController но с ProductSearchService валидацией
   */
  const getProduct = async (productId: string | number): Promise<Product> => {
    return await makeRequest<Product>(`/api/products/${productId}`, {
      method: 'GET',
    })
  }

  /**
   * Получение отзывов товара
   * Backend: ProductReviewController::list()
   */
  const getProductReviews = async (
    productId: string | number,
    params: { page?: number; limit?: number } = {},
  ): Promise<ProductReview[]> => {
    return await makeRequest<ProductReview[]>(
      `/api/products/${productId}/reviews`,
      { method: 'GET' },
      { page: params.page ?? 1, limit: params.limit ?? 10 },
    )
  }

  /**
   * Получение категорий
   * Backend: ProductController::categories()
   */
  const getCategories = async (): Promise<
    Array<{
      id: number
      name: string
      slug: string
      description?: string
      parent_id?: number
      sort_order?: number
    }>
  > => {
    return await makeRequest('/api/products/categories', {
      method: 'GET',
    })
  }

  /**
   * Получение производителей
   * Backend: ProductController::manufacturers()
   */
  const getManufacturers = async (): Promise<
    Array<{
      id: number
      name: string
      slug: string
      description?: string
      website?: string
      country?: string
      sort_order?: number
    }>
  > => {
    return await makeRequest('/api/products/manufacturers', {
      method: 'GET',
    })
  }

  // Методы для админки
  const getProducts = async (params: {
    page?: number
    limit?: number
    search?: string
    status?: string
    category?: number
    manufacturer?: number
    lowStock?: boolean
  }): Promise<{
    products: Product[]
    pagination: {
      page: number
      limit: number
      total: number
      pages: number
    }
    filters: {
      categories: Array<{ id: number; name: string }>
      manufacturers: Array<{ id: number; name: string }>
      statuses: Array<{ value: string; label: string }>
    }
  }> => {
    const response = await makeRequest<
      | {
          products: Product[]
          pagination: {
            page: number
            limit: number
            total: number
            pages: number
          }
          filters: {
            categories: Array<{ id: number; name: string }>
            manufacturers: Array<{ id: number; name: string }>
            statuses: Array<{ value: string; label: string }>
          }
        }
      | ApiEnvelope<{
          products: Product[]
          pagination: {
            page: number
            limit: number
            total: number
            pages: number
          }
          filters: {
            categories: Array<{ id: number; name: string }>
            manufacturers: Array<{ id: number; name: string }>
            statuses: Array<{ value: string; label: string }>
          }
        }>
    >(
      '/api/admin/products',
      {
        method: 'GET',
      },
      params,
    )

    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      response.data &&
      typeof response.data === 'object'
    ) {
      return response.data as typeof response.data & {
        products: Product[]
        pagination: { page: number; limit: number; total: number; pages: number }
        filters: {
          categories: Array<{ id: number; name: string }>
          manufacturers: Array<{ id: number; name: string }>
          statuses: Array<{ value: string; label: string }>
        }
      }
    }

    return response as {
      products: Product[]
      pagination: { page: number; limit: number; total: number; pages: number }
      filters: {
        categories: Array<{ id: number; name: string }>
        manufacturers: Array<{ id: number; name: string }>
        statuses: Array<{ value: string; label: string }>
      }
    }
  }

  const getFormData = async (): Promise<{
    categories: Array<{ id: number; name: string }>
    manufacturers: Array<{ id: number; name: string }>
    attributes: Array<{ id: number; name: string; type: string }>
    units: Array<{ id: number; name: string; symbol: string }>
    statuses: Array<{ value: string; label: string }>
  }> => {
    const response = await makeRequest<
      | {
          categories: Array<{ id: number; name: string }>
          manufacturers: Array<{ id: number; name: string }>
          attributes: Array<{ id: number; name: string; type: string }>
          units: Array<{ id: number; name: string; symbol: string }>
          statuses: Array<{ value: string; label: string }>
        }
      | ApiEnvelope<{
          categories: Array<{ id: number; name: string }>
          manufacturers: Array<{ id: number; name: string }>
          attributes: Array<{ id: number; name: string; type: string }>
          units: Array<{ id: number; name: string; symbol: string }>
          statuses: Array<{ value: string; label: string }>
        }>
    >('/api/admin/products/form-data', {
      method: 'GET',
    })

    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      response.data &&
      typeof response.data === 'object'
    ) {
      return response.data as {
        categories: Array<{ id: number; name: string }>
        manufacturers: Array<{ id: number; name: string }>
        attributes: Array<{ id: number; name: string; type: string }>
        units: Array<{ id: number; name: string; symbol: string }>
        statuses: Array<{ value: string; label: string }>
      }
    }

    return response as {
      categories: Array<{ id: number; name: string }>
      manufacturers: Array<{ id: number; name: string }>
      attributes: Array<{ id: number; name: string; type: string }>
      units: Array<{ id: number; name: string; symbol: string }>
      statuses: Array<{ value: string; label: string }>
    }
  }

  const deleteProduct = async (id: number): Promise<void> => {
    return await makeRequest(`/api/admin/products/${id}`, {
      method: 'DELETE',
    })
  }

  const getAdminProduct = async (id: number): Promise<Product> => {
    const response = await makeRequest<Product | ApiEnvelope<Product>>(
      `/api/admin/products/${id}`,
      {
        method: 'GET',
      },
    )

    throwIfApiError(response)

    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      response.data &&
      typeof response.data === 'object'
    ) {
      return response.data as Product
    }

    return response as Product
  }

  const createProduct = async (data: AdminProductPayload): Promise<Product> => {
    const response = await makeRequest<Product | ApiEnvelope<Product>>('/api/admin/products', {
      method: 'POST',
      body: JSON.stringify(data),
    })

    throwIfApiError(response)

    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      response.data &&
      typeof response.data === 'object'
    ) {
      return response.data as Product
    }

    return response as Product
  }

  const updateProduct = async (id: number, data: AdminProductPayload): Promise<Product> => {
    const response = await makeRequest<Product | ApiEnvelope<Product>>(
      `/api/admin/products/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    )

    throwIfApiError(response)

    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      response.data &&
      typeof response.data === 'object'
    ) {
      return response.data as Product
    }

    return response as Product
  }

  const bulkAction = async (data: {
    action: 'activate' | 'deactivate' | 'delete'
    ids: number[]
  }): Promise<{ message: string; count: number }> => {
    return await makeRequest('/api/admin/products/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  return {
    searchProducts,
    quickSearch,
    getPopularProducts,
    getProduct,
    getProductReviews,
    getCategories,
    getManufacturers,
    getProducts,
    getFormData,
    getAdminProduct,
    createProduct,
    updateProduct,
    deleteProduct,
    bulkAction,
  }
}

/**
 * Преобразует данные продукта из API в формат для компонентов
 */
export function transformProduct(apiProduct: Record<string, unknown>) {
  const apiBaseUrl = getApiBaseUrl()
  const parseProductId = (value: unknown): number => {
    if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
      return value
    }

    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (/^\d+$/.test(trimmed)) {
        const parsed = Number.parseInt(trimmed, 10)
        return Number.isInteger(parsed) && parsed > 0 ? parsed : 0
      }
    }

    return 0
  }
  const isSeededImagePlaceholder = (img: string): boolean => {
    const normalized = img.split('/').pop() ?? img
    return (
      /^(product|microscope|centrifuge|spectrophotometer|chromatograph|pipette|filter|scale)_\d*_(main|stage|rotor|module|cell|tip|panel|detail|\d+)\.jpg$/i.test(
        normalized,
      ) || /^product__[\w]+\.jpg$/i.test(normalized)
    )
  }
  const normalizeImageUrl = (img: string): string => {
    if (!img) return ''
    if (isSeededImagePlaceholder(img)) return '/placeholder-product.svg'
    if (img.startsWith('http')) return img
    if (img.startsWith('/')) return `${apiBaseUrl}${img}`
    return `${apiBaseUrl}/uploads/products/images/${img}`
  }

  // Обработка цены - объект Money {amount: 5314400, currency: 'RUB'}
  // amount в копейках, делим на 100 для получения рублей
  let price = 0
  const priceValue = apiProduct['price']
  if (
    priceValue &&
    typeof priceValue === 'object' &&
    priceValue !== null &&
    !Array.isArray(priceValue)
  ) {
    const moneyObj = priceValue as { amount: number; currency: string }
    if (typeof moneyObj.amount === 'number') {
      price = moneyObj.amount / 100
    }
  }

  // Обработка изображений - может быть массив строк или массив объектов
  let images: string[] = []
  const imagesValue = apiProduct['images']
  const primaryImageUrl =
    (apiProduct['primary_image_url'] as string | undefined) ??
    (apiProduct['primaryImageUrl'] as string | undefined)

  if (Array.isArray(imagesValue) && imagesValue.length > 0) {
    // Проверяем, массив строк или объектов
    if (typeof imagesValue[0] === 'string') {
      // Массив строк (из списка товаров)
      images = (imagesValue as string[])
        .filter((img: string) => img && img.trim() !== '')
        .map((img: string) => normalizeImageUrl(img))
    } else if (typeof imagesValue[0] === 'object') {
      // Массив объектов (из деталей товара)
      images = (imagesValue as Array<{ path?: string; filename?: string }>)
        .filter((img) => img && (img.path || img.filename))
        .map((img) => normalizeImageUrl(img.path || img.filename || ''))
    }
  } else if (typeof primaryImageUrl === 'string' && primaryImageUrl.length > 0) {
    const imgUrl = primaryImageUrl
    if (imgUrl.trim() !== '') {
      images = [normalizeImageUrl(imgUrl)]
    }
  }

  if (images.length === 0) {
    images = ['/placeholder-product.svg']
  }

  const stockValue = apiProduct['stock']
  const isAvailableValue = apiProduct['is_available'] ?? apiProduct['isAvailable']
  const minimumOrderValue = apiProduct['minimum_order'] ?? apiProduct['minimumOrder']
  const minimumOrder =
    typeof minimumOrderValue === 'number' && Number.isInteger(minimumOrderValue) && minimumOrderValue > 0
      ? minimumOrderValue
      : undefined
  const normalizedProductId = parseProductId(apiProduct['id'] ?? apiProduct['product_id'])
  const result = {
    id: normalizedProductId,
    sku: apiProduct['sku'],
    name: apiProduct['name'],
    description: apiProduct['description'],
    price: price,
    currency:
      (apiProduct['price'] as { currency?: string })?.currency || apiProduct['currency'] || 'RUB',
    imageUrl: images[0] || '/placeholder-product.svg',
    images: images,
    inStock:
      Boolean(isAvailableValue ?? (typeof stockValue === 'number' ? stockValue > 0 : false)) &&
      (typeof stockValue === 'number' ? stockValue > 0 : true),
    stockQuantity: (stockValue as number) || 0,
    minimumOrder,
    unit: (() => {
      const u = apiProduct['unit'] as
        | { id?: number; name?: string; symbol?: string }
        | null
        | undefined
      if (u && typeof u === 'object') {
        return { id: Number(u.id ?? 0), name: String(u.name ?? ''), symbol: String(u.symbol ?? '') }
      }
      return null
    })(),
    rating: (apiProduct['rating'] as number) || 0,
    reviewCount: (apiProduct['reviewCount'] as number) || 0,
    manufacturerId: (apiProduct['manufacturer'] as { id?: number })?.id || null,
    manufacturerName: (apiProduct['manufacturer'] as { name?: string })?.name || null,
    categoryId: (apiProduct['category'] as { id?: number })?.id || null,
    categoryName: (apiProduct['category'] as { name?: string })?.name || null,
    specifications: (apiProduct['specifications'] as Record<string, unknown>) || {},
    isNew: (apiProduct['isNew'] as boolean) || false,
    isBestseller: (apiProduct['isBestseller'] as boolean) || false,
    createdAt: (apiProduct['createdAt'] as string) || '',
    updatedAt: (apiProduct['updatedAt'] as string) || '',
  }

  return result
}

// Экспорт для обратной совместимости - создаем только при вызове
export const productApi = () => useProductAPI()
